import { SearchDomain, SearchResource } from '@qualio/ui-components';
import { StandardValueObject } from '../components/EventFormField/EventFormField.types';
import { FieldType, FieldTypeSchema, QObjectFieldType, QObjectFieldTypes } from '../types/formFields/common';
import { NewFormTemplateField } from '../types/formFields/formTemplates';
import { alwaysClone, modifiedToString } from './functionalUtils';

// Maps a QObjectFieldType to its quicksearch v2 "resource" key(s).
export const qObjectResourceMap = {
  event: 'quality-event',
  document: 'document',
  user: 'user',
  supplier: 'supplier',
  change_request: 'change-request',
  registry: 'registry-item',
  design_controls: ['dc-requirement', 'dc-testCase', 'dc-change-control', 'dc-risk'],
  training_plan: 'training-plan',
  document_template: 'document-template',
  event_template: 'event-template',
  task: 'task',
} as const satisfies Record<QObjectFieldType, SearchResource | readonly SearchResource[] | SearchDomain>;

type TransformerFunction<T> = (value: T) => string;

// Used to create a single Field "StandardValueObject" from a value
export const createFieldValueObject = <T>(
  value: T,
  fieldId: number,
  existingData?: StandardValueObject,
  transformer?: TransformerFunction<T>,
) => {
  const guardedTransformer = transformer ?? modifiedToString;

  return {
    ...existingData,
    form_field_id: fieldId,
    value: guardedTransformer(value),
  };
};

/**
 * Used to create a Field "ValueObject" array from a single value, or an empty
 * array if the value is undefined
 */
export const createFieldValueArray = <T>(
  value: T | undefined | null,
  fieldId: number,
  existingData?: StandardValueObject,
  transformer?: TransformerFunction<T>,
) => {
  return value ? [createFieldValueObject(value, fieldId, existingData, transformer)] : [];
};

/**
 * Use to check if the given field type is one that has "options" (i.e. dropdown, radio, checkbox)
 */
export const isFieldWithOptions = (fieldType: FieldType) => {
  const fieldsWithOptions: FieldType[] = [
    FieldTypeSchema.enum.dropdown,
    FieldTypeSchema.enum.checkbox,
    FieldTypeSchema.enum.radio,
  ];

  return fieldsWithOptions.includes(fieldType);
};

const getBaseFieldObject = alwaysClone({
  form_template_id: null,
  label: '',
  helptext: '',
  default_content: '',
  mandatory: false,
  order: null,
  multi: false,
});

const generateOptionList = alwaysClone({
  options: [{ value: '' }, { value: '' }, { value: '' }],
});

const generateAttachmentField = alwaysClone({
  ...getBaseFieldObject(),
  type: FieldTypeSchema.enum.attachment,
  multi: true,
});

const generateDropdownField = alwaysClone({
  ...getBaseFieldObject(),
  type: FieldTypeSchema.enum.dropdown,
  form_option_list: generateOptionList(),
});

const generateRadioField = alwaysClone({
  ...getBaseFieldObject(),
  type: FieldTypeSchema.enum.radio,
  form_option_list: generateOptionList(),
  multi: false,
});

const generateCheckboxField = alwaysClone({
  ...getBaseFieldObject(),
  type: FieldTypeSchema.enum.checkbox,
  form_option_list: generateOptionList(),
  multi: true,
});

const generateBasicField = (type: FieldType) => ({
  ...getBaseFieldObject(),
  type,
});

const fieldGenerators = {
  [FieldTypeSchema.enum.attachment]: generateAttachmentField,
  [FieldTypeSchema.enum.dropdown]: generateDropdownField,
  [FieldTypeSchema.enum.radio]: generateRadioField,
  [FieldTypeSchema.enum.checkbox]: generateCheckboxField,
  [FieldTypeSchema.enum.text]: () => generateBasicField(FieldTypeSchema.enum.text),
  [FieldTypeSchema.enum.numeric]: () => generateBasicField(FieldTypeSchema.enum.numeric),
  [FieldTypeSchema.enum.boolean]: () => generateBasicField(FieldTypeSchema.enum.boolean),
  [FieldTypeSchema.enum.date]: () => generateBasicField(FieldTypeSchema.enum.date),
  [FieldTypeSchema.enum.rich_text]: () => generateBasicField(FieldTypeSchema.enum.rich_text),
  [FieldTypeSchema.enum.section]: () => generateBasicField(FieldTypeSchema.enum.section),
  [FieldTypeSchema.enum.document]: () => generateBasicField(FieldTypeSchema.enum.document),
  [FieldTypeSchema.enum.event]: () => generateBasicField(FieldTypeSchema.enum.event),
  [FieldTypeSchema.enum.supplier]: () => generateBasicField(FieldTypeSchema.enum.supplier),
  [FieldTypeSchema.enum.user]: () => generateBasicField(FieldTypeSchema.enum.user),
  [FieldTypeSchema.enum.change_request]: () => generateBasicField(FieldTypeSchema.enum.change_request),
  [FieldTypeSchema.enum.design_controls]: () => generateBasicField(FieldTypeSchema.enum.design_controls),
  [FieldTypeSchema.enum.training_plan]: () => generateBasicField(FieldTypeSchema.enum.training_plan),
  [FieldTypeSchema.enum.document_template]: () => generateBasicField(FieldTypeSchema.enum.document_template),
  [FieldTypeSchema.enum.event_template]: () => generateBasicField(FieldTypeSchema.enum.event_template),
  [FieldTypeSchema.enum.task]: () => generateBasicField(FieldTypeSchema.enum.task),
};

const registryFieldGenerator = (resourceSubtype?: string) => ({
  ...getBaseFieldObject(),
  type: 'registry',
  resource_sub_type: resourceSubtype,
});

/**
 * Generate a new Template Form Field object for the given field type
 * Note: Code smell here with the type assertion, but as of now it is needed as
 * typescript seems unable to infer the correct type because NewFormTemplateField
 * is a discriminated union where the "standard" should not have a form_option_list
 */
export const generateNewFormField = (
  fieldType: FieldType,
  formTemplateId: number,
  order: number,
  resourceSubtype?: string,
): NewFormTemplateField => {
  const baseGenerator =
    fieldType === 'registry' ? () => registryFieldGenerator(resourceSubtype) : fieldGenerators[fieldType];

  return {
    ...baseGenerator(),
    form_template_id: formTemplateId,
    order,
  } as NewFormTemplateField;
};

/**
 * Check if the given field type is one of the form fields corresponding to the
 * QSelect form fields
 */
export const fieldTypeIsQObjectType = (fieldType: FieldType): fieldType is QObjectFieldType => {
  return QObjectFieldTypes.includes(fieldType as QObjectFieldType);
};
